'use client';

import type { ReactElement } from 'react';
import { cn } from '../../lib/cn';
import { type LinkLike, NativeLink } from '../../lib/link';

/**
 * Wordmark di SLIMS CER: due asset distinti, scambiati in base al tema (cfr. il
 * mock `SLIMS-CER_v6.html`: `logo.src = theme === 'light' ? srcLight : srcDark`).
 *
 * Gli asset vivono in `public/` dell'app; i percorsi restano qui perché "quale
 * file per quale tema" è una decisione di presentazione.
 */

export type BrandTheme = 'light' | 'dark';

/**
 * Taglia del wordmark: `header` (28px) è quella del chrome, allineata ai controlli
 * dell'header; `auth` (40px) è quella delle pagine di login, dove il logo è
 * l'elemento principale della schermata.
 */
export type BrandLogoSize = 'header' | 'auth';

export interface BrandLogoProps {
	/** Tema corrente dell'app: seleziona la variante del wordmark. */
	theme: BrandTheme;
	/** Taglia del wordmark. Default: `header`. */
	size?: BrandLogoSize;
	/** Se presente il logo diventa un link (via `linkComponent`). */
	href?: string;
	/** Componente link da usare (default: link nativo `<a>`). */
	linkComponent?: LinkLike;
	/**
	 * White-label (WB-R22): URL di un logo custom della CER. Se valorizzato,
	 * {@link CerLogo} lo mostra al posto del wordmark SLIMS, un po' più alto e con
	 * un tetto di larghezza; la larghezza segue le proporzioni dell'asset (vedi
	 * {@link CUSTOM_LOGO_BOX}).
	 */
	logoUrl?: string | null;
	/** Nome della CER white-label, usato come testo alternativo del logo custom. */
	appName?: string | null;
}

const LOGO_HEIGHT: Record<BrandLogoSize, string> = {
	header: 'h-7',
	auth: 'h-10',
};

/**
 * Logo white-label: più alto del wordmark SLIMS (spesso è un marchio compatto,
 * non una scritta larga) e con un tetto di larghezza perché un lockup molto
 * orizzontale non spinga via i controlli dell'header. `w-auto` fa seguire alla
 * larghezza le proporzioni reali dell'immagine (un asset quadrato resta
 * quadrato, uno largo resta largo); `object-contain` interviene solo se il logo
 * supera il tetto, rimpicciolendolo invece di schiacciarlo.
 */
const CUSTOM_LOGO_BOX: Record<BrandLogoSize, string> = {
	header: 'h-10 max-w-[220px]',
	auth: 'h-14 max-w-[320px]',
};

/** Il resto del riquadro: la larghezza segue le proporzioni dell'asset. */
const LOGO_CLASS = 'w-auto flex-none';

/** Avvolge il wordmark in un link quando `href` è presente. */
function withLink(
	logo: ReactElement,
	href: string | undefined,
	Link: LinkLike = NativeLink,
): ReactElement {
	if (!href) return logo;
	return (
		<Link href={href} className="flex-none transition-opacity hover:opacity-80">
			{logo}
		</Link>
	);
}

/**
 * Wordmark SLIMS CER: asset dedicato per tema chiaro e scuro. Con `logoUrl`
 * (white-label WB-R22) mostra invece il logo custom della CER: stessa altezza
 * del wordmark, larghezza proporzionale all'asset, unico per entrambi i temi
 * (il branding fornisce un solo logo).
 */
export function CerLogo({
	theme,
	size = 'header',
	href,
	linkComponent,
	logoUrl,
	appName,
}: BrandLogoProps) {
	return withLink(
		<img
			src={logoUrl || (theme === 'light' ? '/logo.png' : '/logo-dark.png')}
			alt={logoUrl ? appName || 'Logo CER' : 'SLIMS CER'}
			className={cn(
				logoUrl ? cn(CUSTOM_LOGO_BOX[size], 'object-contain object-left') : LOGO_HEIGHT[size],
				LOGO_CLASS,
			)}
		/>,
		href,
		linkComponent,
	);
}
